import React from 'react';

const TermsAndCondition = () => {
    return (
        <div className="w-full sm:w-5/6 md:w-3/4 lg:w-4/5 xl:w-4/5 mx-auto py-12 px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl border border-black shadow-lg overflow-hidden">
                <div className="p-6 sm:p-8 md:p-10">
                    {/* Main heading */}
                    <h1 className="text-center text-4xl md:text-5xl font-black text-[#5c3e21] mb-6">
                        Terms and Conditions
                    </h1>

                    {/* Subtitle / Company */}
                    <p className="text-start text-gray-500 text-sm mb-8">
                        Acharya For Life Solutions Limited, trading as Smart Acharya
                        <br />
                        Last updated: 28/05/2026
                    </p>

                    {/* 1. Introduction */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">1. Introduction</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-4">
                        These Terms govern access to and use of the AI-powered educational platform operated by
                        Acharya for Life Solutions Limited (“we”, “us”, “our”).
                    </p>
                    <p className="text-gray-800 text-lg leading-relaxed mb-2">These Terms apply to:</p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-4">
                        <li>Individual users (students, parents, educators)</li>
                        <li>Schools and educational institutions purchasing access</li>
                    </ul>
                    <p className="text-gray-800 text-lg leading-relaxed mb-6">
                        By using the Platform, you agree to these Terms.
                    </p>

                    {/* 2. Contract Structure */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">2. Contract Structure</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-2">
                        Depending on how you access the Platform:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-4">
                        <li><strong>Individual users (B2C)</strong>: contract directly with us under these Terms</li>
                        <li><strong>Schools (B2B)</strong>: these Terms apply alongside any separate agreement (e.g. Order Form or Data Processing Agreement)</li>
                    </ul>
                    <p className="text-gray-800 text-lg leading-relaxed mb-6">
                        If there is a conflict, the school agreement takes precedence.
                    </p>

                    {/* 3. Eligibility and Safeguarding */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">3. Eligibility and Safeguarding</h2>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-6">
                        <li>Users under 18 must have authorisation from a parent, guardian, or school</li>
                        <li>Schools are responsible for ensuring appropriate consent and supervision</li>
                        <li>We implement safeguards but do not replace teacher or parental oversight</li>
                    </ul>

                    {/* 4. Accounts */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">4. Accounts</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-2">You must:</p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-4">
                        <li>Provide accurate information</li>
                        <li>Keep login details secure</li>
                        <li>Use the Platform only as authorised</li>
                    </ul>
                    <p className="text-gray-800 text-lg leading-relaxed mb-2">We may suspend accounts to:</p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-6">
                        <li>Prevent misuse</li>
                        <li>Address safeguarding risks</li>
                        <li>Comply with legal obligations</li>
                    </ul>

                    {/* 5. Services */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">5. Services</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-2">The Platform provides:</p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-4">
                        <li>AI-generated tutoring and explanations</li>
                        <li>Personalised learning pathways</li>
                        <li>Progress tracking and analytics</li>
                    </ul>
                    <p className="text-gray-800 text-lg leading-relaxed mb-6">
                        We may update or modify features at any time.
                    </p>

                    {/* 6. AI Disclaimer */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">6. AI Disclaimer (Important)</h2>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-4">
                        <li>AI outputs are automated and may be inaccurate or incomplete</li>
                        <li>Content is for <strong>educational support only</strong>, not a substitute for teaching</li>
                        <li>We do <strong>not guarantee academic performance or outcomes</strong></li>
                        <li>Users should verify important information</li>
                    </ul>
                    <p className="text-gray-800 text-lg leading-relaxed mb-6">
                        We do not make fully automated decisions with significant effects without human involvement.
                    </p>

                    {/* 7. Acceptable Use */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">7. Acceptable Use</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-2">You must not:</p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-4">
                        <li>Use the Platform to cheat in exams or submit AI-generated work where prohibited</li>
                        <li>Input harmful, abusive, or illegal content</li>
                        <li>Attempt to reverse engineer or exploit AI systems</li>
                        <li>Use the Platform in a way that could harm other users, especially minors</li>
                    </ul>
                    <p className="text-gray-800 text-lg leading-relaxed mb-6">
                        We may remove content and suspend access if needed.
                    </p>

                    {/* 8. Fees, Billing, and Subscriptions */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">8. Fees, Billing, and Subscriptions</h2>
                    <p className="text-gray-800 text-lg leading-relaxed font-semibold mb-2">Individual Users:</p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-4">
                        <li>Subscriptions renew automatically unless cancelled</li>
                        <li>Free trials (if offered) convert to paid unless cancelled before expiry</li>
                        <li>You may cancel at any time; access continues until the end of the billing period</li>
                    </ul>
                    <p className="text-gray-800 text-lg leading-relaxed font-semibold mb-2">Schools:</p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-4">
                        <li>Fees are agreed via contract or order form</li>
                        <li>Payment terms are set in the applicable agreement</li>
                    </ul>
                    <p className="text-gray-800 text-lg leading-relaxed mb-6">
                        Unless required by law, fees are non-refundable.
                    </p>

                    {/* 9. Intellectual Property */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">9. Intellectual Property</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-2">
                        We own or license all rights in the Platform.
                    </p>
                    <p className="text-gray-800 text-lg leading-relaxed mb-4">
                        You are granted a limited licence to use it for educational purposes only.
                    </p>
                    <p className="text-gray-800 text-lg leading-relaxed font-semibold mb-2">User Content:</p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-6">
                        <li>You retain ownership</li>
                        <li>You grant us a licence to:
                            <ul className="list-[circle] pl-6 mt-1">
                                <li>Operate the Platform</li>
                                <li>Improve AI systems (in anonymised or aggregated form)</li>
                            </ul>
                        </li>
                    </ul>

                    {/* 10. Data Protection */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">10. Data Protection</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-4">
                        We process personal data in accordance with our Privacy Policy.
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-6">
                        <li>For schools: we act as a <strong>data processor</strong></li>
                        <li>For individual users: we act as a <strong>data controller</strong></li>
                    </ul>

                    {/* 11. Availability and Support */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">11. Availability and Support</h2>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-4">
                        <li>We aim for high availability but do not guarantee uninterrupted service</li>
                        <li>Maintenance and updates may affect access</li>
                        <li>Support is provided via: support@smartacharya.com</li>
                    </ul>

                    {/* 12. Limitation of Liability */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">12. Limitation of Liability</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-2">
                        To the fullest extent permitted by law:
                    </p>
                    <p className="text-gray-800 text-lg leading-relaxed mb-2">We are not liable for:</p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-4">
                        <li>Loss of grades, academic results, or opportunities</li>
                        <li>Reliance on AI-generated content</li>
                        <li>Indirect or consequential losses</li>
                    </ul>
                    <p className="text-gray-800 text-lg leading-relaxed mb-2">Our total liability is limited to:</p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-4">
                        <li>Fees paid in the previous 12 months (if any)</li>
                    </ul>
                    <p className="text-gray-800 text-lg leading-relaxed mb-2">Nothing excludes liability for:</p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-6">
                        <li>Death or personal injury due to negligence</li>
                        <li>Fraud or fraudulent misrepresentation</li>
                    </ul>

                    {/* 13. Termination */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">13. Termination</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-2">We may suspend or terminate access:</p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-4">
                        <li>For breach of these Terms</li>
                        <li>For safeguarding concerns</li>
                        <li>If required by law</li>
                    </ul>
                    <p className="text-gray-800 text-lg leading-relaxed mb-6">
                        Schools may terminate in accordance with their contract.
                    </p>

                    {/* 14. Safeguarding and Monitoring */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">14. Safeguarding and Monitoring</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-2">We may:</p>
                    <ul className="list-disc pl-6 space-y-1 text-gray-800 text-lg mb-4">
                        <li>Monitor interactions for safety</li>
                        <li>Remove harmful content</li>
                        <li>Report serious concerns to authorities</li>
                    </ul>
                    <p className="text-gray-800 text-lg leading-relaxed mb-6">
                        This is especially important where users are under 18.
                    </p>

                    {/* 15. Third-Party Services */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">15. Third-Party Services</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-4">
                        The Platform may integrate with third parties (e.g. login providers, payment processors).
                    </p>
                    <p className="text-gray-800 text-lg leading-relaxed mb-6">
                        We are not responsible for third-party services.
                    </p>

                    {/* 16. Changes to Terms */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">16. Changes to Terms</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-4">
                        We may update these Terms.
                    </p>
                    <p className="text-gray-800 text-lg leading-relaxed mb-6">
                        Material changes will be communicated where appropriate.
                    </p>

                    {/* 17. Governing Law */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">17. Governing Law</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-4">
                        These Terms are governed by the laws of England and Wales.
                    </p>
                    <p className="text-gray-800 text-lg leading-relaxed mb-6">
                        Courts of England and Wales have exclusive jurisdiction.
                    </p>

                    {/* 18. Contact */}
                    <h2 className="text-2xl font-bold text-[#8c5021] mt-6 mb-3">18. Contact</h2>
                    <p className="text-gray-800 text-lg leading-relaxed mb-2">Acharya for Life Solutions Limited</p>
                    <p className="text-gray-800 text-lg leading-relaxed mb-2">Email: info@smartacharya.com</p>
                    <p className="text-gray-800 text-lg leading-relaxed">Address: 23 Kings Reach, Slough, SL3 7QE</p>
                </div>
            </div>
        </div>
    );
};

export default TermsAndCondition;